<template>
  <div>
    <v-row
      v-for="(id, index) in filteredSubsidyProgramIds"
      :key="id"
      :class="getClasses(index)"
    >
      <v-col
        class="pb-0"
        cols="12"
        lg="4"
        xl="3"
      >
        <h3 class="fs-18 fw-600">
          {{ $bct(`${terms.programs} offered`) }}
        </h3>
        <div class="fs-14">
          {{ getSubsidyProgramName(id) }}
        </div>
      </v-col>
      <v-col
        class="c-black fs-16 fw-400"
        cols="12"
        lg="8"
        xl="9"
      >
        <div>
          <Program
            v-for="(program, programIndex) in programsBySubsidy[id]"
            :key="program.id"
            :class="programIndex != 0 ? 'mt-4' : ''"
            :program="program"
            :rows="rows"
            :show-availability="isAvailabilityFlagEnabled(id)"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import _ from 'lodash';

import Program from '@/shared/components/search/program.vue';
import useApi from '@/shared/composables/useApi';
import useRolePermissions from '@/shared/composables/useRolePermissions';
import useTerms from '@/shared/composables/useTerms';

const { api: Api } = useApi();
const { subsidiesAdmin } = useRolePermissions();
const { terms } = useTerms();

const $bct = inject('translator');

const UNASSOCIATED_PROGRAM_ID_VALUE = 'null';

const props = defineProps({
  hideTopBorder: {
    type: Boolean,
    default: false,
  },
  provider: {
    type: Object,
    default: null,
  },
  rows: {
    type: Number,
    default: null,
  },
});

const programsBySubsidy = ref({});
const subsidyPrograms = ref([]);

const filteredSubsidyProgramIds = computed(() => {
  const ids = Object.keys(programsBySubsidy.value);
  const hasUnassociatedPrograms = ids.includes(UNASSOCIATED_PROGRAM_ID_VALUE);

  const filteredIds = ids.filter((id) => subsidyPrograms.value.find((sp) => sp.id === id));

  if (hasUnassociatedPrograms) filteredIds.unshift(UNASSOCIATED_PROGRAM_ID_VALUE);

  return filteredIds;
});

watch(
  () => props.provider,
  (newVal) => {
    if (newVal) void loadPrograms();
  },
);

onMounted(() => {
  void loadPrograms();
  void loadSubsidyPrograms();
});

function isAvailabilityFlagEnabled(id) {
  const subsidyProgram = subsidyPrograms.value.find((sp) => sp.id === id);
  return subsidyProgram?.enable_show_seat_availability;
}

function getClasses(index) {
  if (props.hideTopBorder && index === 0) return 'c-black mb-3 pt-1';
  return 'bt-1 bc-very-light-gray c-black mb-3 pt-1';
}

function getSubsidyProgramName(id) {
  if (id === UNASSOCIATED_PROGRAM_ID_VALUE) return null;
  return subsidyPrograms.value.find((sp) => sp.id === id)?.name;
}

async function loadPrograms() {
  const params = {
    provider_id: props.provider.id,
    include: 'seat_availability_flag',
  };
  const { data } = await Api.publicApi.organization.program.index(params);
  programsBySubsidy.value = _.groupBy(data, 'subsidy_program_id');
}

async function loadSubsidyPrograms() {
  let resp;

  if (subsidiesAdmin) {
    resp = await Api.organization.subsidy_program.index();
  } else {
    resp = await Api.shared.subsidyProgram.index();
  }

  subsidyPrograms.value = resp.data;
}
</script>
